import type { ReactNode } from 'react'
import { Badge, Button, Tooltip, TooltipTextOverflow } from '@systemeio/components'
import { PlusCircleIcon, ClockIcon } from '@systemeio/icons'
import { useTranslation } from 'next-i18next'
import { useActivityRelativeDate } from 'shared/hooks/use-community-relative-date'
import type { InvitationInterface } from 'modules/community/types/invitation-interface'

interface InvitationShortProps {
  invitation: InvitationInterface
  onResend?: (invitation: InvitationInterface) => void
  onCancel?: (invitation: InvitationInterface) => void
  onReinvite?: (invitation: InvitationInterface) => void
}

// TODO: extract the pending/expired action blocks into separate components instead
// of inlining them with ternary operators.
// TODO: research whether onResend/onCancel/onReinvite should be consolidated into a
// single `actions` prop or refactored to a different callback shape.

export const InvitationShort = ({
  invitation,
  onResend,
  onCancel,
  onReinvite,
}: InvitationShortProps): ReactNode => {
  const { t } = useTranslation()
  const invitedAtRelative = useActivityRelativeDate(invitation.invitedAt)

  return (
    <div
      data-testid="invitation-short"
      className="group relative flex size-full flex-col items-center overflow-hidden rounded-lg bg-bg-primary px-5 py-7 lg:px-7 lg:py-9"
    >
      <img
        src={invitation.avatarUrl ?? '/default_post_author_avatar.webp'}
        alt={invitation.email}
        className="mx-auto mb-3 block size-14 rounded-full sm:mx-0 sm:shrink-0 lg:size-20"
      />

      <div className="flex w-full flex-col items-center">
        <TooltipTextOverflow
          className="block w-full truncate text-center text-sm text-text-link"
          tooltipContent={invitation.email}
        >
          {invitation.email}
        </TooltipTextOverflow>
        <div className="mt-1">
          {invitation.status === 'pending' && (
            <Tooltip
              trigger={
                <Badge color="yellow">{t(`members.invitation.status.${invitation.status}`)}</Badge>
              }
              content={t('members.pending_invitation.tooltip')}
            />
          )}
        </div>

        {invitation.status === 'pending' && (onResend || onCancel) && (
          <div className="mt-3 flex w-full flex-col gap-2">
            {onResend && (
              <Button
                variant="primary"
                onClick={() => {
                  onResend(invitation)
                }}
                className="h-auto min-h-10 w-full gap-1.5 whitespace-normal"
              >
                <ClockIcon className="size-4" />
                {t('members.invitation.resend')}
              </Button>
            )}
            {onCancel && (
              <Button
                variant="secondary"
                onClick={() => {
                  onCancel(invitation)
                }}
                className="h-auto min-h-10 w-full gap-1.5 whitespace-normal"
              >
                {t('confirmation_modal.cancel')}
              </Button>
            )}
          </div>
        )}
        {invitation.status === 'expired' && onReinvite && (
          <div className="mt-2 w-full">
            <Button
              variant="primary"
              onClick={() => {
                onReinvite(invitation)
              }}
              className="h-auto min-h-10 w-full gap-1.5 whitespace-normal"
            >
              <PlusCircleIcon className="size-4" />
              {t('members.invitation.reinvite')}
            </Button>
          </div>
        )}
        <span className="mt-2 text-sm font-normal text-text-tertiary">
          {t('members.invitation.invited_on')} {invitedAtRelative}
        </span>
      </div>
    </div>
  )
}
